// =========================================================
// New copy for the landing: only what Pages/Home/content.js and the CMS do not
// already say. Existing copy is imported from there, not repeated here.
//
// Same rule as content.js: every sentence is a product fact. The source of
// each one is written beside it. `{trialDays}` comes from the live pricing
// overlay through fillVars, and a line whose number is unknown is dropped
// rather than guessed.
//
// Words, not letters, are animated on this page, so no line here may rely on
// letter-spacing or per-letter effects.
//
// `chapter` is a DAY.items id; its anchor is `#day-<id>` (see Pillars.js).
// `capture` and `photo` are keys of CAPTURES and PHOTOS in media.js.
// `icon` is a lucide-react component.
// =========================================================

import {
  BookCheck,
  CalendarCheck,
  ClipboardCheck,
  CreditCard,
  LayoutDashboard,
  MessageCircle,
  NotebookPen,
  PackageSearch,
  RefreshCcwDot,
  Sheet,
  ShieldCheck,
  Smartphone,
  Store,
  Users,
  Wallet,
  Wrench,
} from "lucide-react";

// What the page says when the CMS row is empty, so the hero is never blank.
// Same sentences as the seed in Back-End/Controller/HomeContent.js.
export const HERO_FALLBACK = {
  title: "خليك",
  titleAccent: "الريّس.",
  subtitle: "نظام تشغيل واحد لشركات التكييف — المشاوير والأسطوات والعهدة والخزنة في شاشة واحدة.",
  ctaText: "ابدأ شهرك المجاني", // content.js HOW.ctaText; data.js fits it to the trial length
};

// Every "start" button when the owner has closed signups (Settings).
export const SIGNUPS_CLOSED_CTA = { text: "تواصل معنا", href: "#contact" };

export const CONTACT_FALLBACK = {
  eyebrow: "كلّمنا",
  titleAccent: "اتكلم",
  titleRest: " مع الريّس النهاردة",
  subtitle: "عايز تشوف الريّس على شغلك قبل ما تشترك؟ سيب بياناتك وهنرجعلك في أقرب وقت.",
};

// Hero's second button: the phone when the CMS has one, otherwise the price.
export const HERO_SECONDARY = {
  phone: "اتصل بينا",
  pricing: "اعرف السعر", // the spec's wording; links to #pricing
  contact: "كلّمنا", // when there is neither a phone nor a plan on sale
};

// The floating bar under the hero. All four are in content.js FAQ.
export const TRUST = [
  // FAQ "card": «{trialDays} يوم بكل المزايا»
  { id: "trial", icon: CalendarCheck, text: "{trialDays} يوم مجانًا بكل المزايا" },
  // FAQ "card": «هطلب كارت في التجربة؟ لأ.»
  { id: "card", icon: CreditCard, text: "من غير كارت" },
  // FAQ "card": «ومفيش تجديد تلقائي» (Controller/Payments.js, one invoice per period)
  { id: "renewal", icon: RefreshCcwDot, text: "مفيش تجديد تلقائي" },
  // FAQ "data": «بيانات كل شركة معزولة عن غيرها»
  { id: "data", icon: ShieldCheck, text: "بياناتك معزولة عن غيرك" },
];

// «قبل الريّس». The first half of each card is the old way, in the words of
// STORY.text and STORY.tools (illustrations, not claims). The fix is DAY's.
export const PROBLEMS = {
  // STORY.text: «وآخر اليوم بتلف على الأسطوات بالتليفون»
  title: "ليه لسه بتلف على الأسطوات بالتليفون؟",
  answerLabel: "الريّس بيعمل إيه",
  items: [
    {
      id: "jobs",
      icon: MessageCircle,
      // STORY.text «المشوار في الواتساب»
      title: "المشوار في الواتساب",
      // STORY.tools chat, STORY.text «مين خلّص إيه»
      line: "«ابعتلي العنوان تاني؟» وآخر اليوم مش عارف مين خلّص إيه.",
      // DAY jobs: «الطلب بيتفتح، يروح لأسطى أو أكتر، ويرجعلك للمراجعة»
      fix: "المشوار بيتفتح، يروح لأسطى أو أكتر، ويرجعلك للمراجعة قبل ما يتقفل.",
      chapter: "jobs",
      link: "شوف المشاوير",
    },
    {
      id: "stock",
      icon: NotebookPen,
      // STORY.text «العهدة في دفتر»
      title: "العهدة في دفتر",
      // STORY.tools book
      line: "«2 كمبروسر عهدة… مع مين؟» والإجابة في ورقة.",
      // DAY stock title
      fix: "كل قطعة: مع مين، وامتى نزلت، وبكام.",
      chapter: "stock",
      link: "شوف العهدة",
    },
    {
      id: "money",
      icon: Sheet,
      // STORY.text «الفلوس في إكسل»
      title: "الفلوس في إكسل",
      // STORY.tools sheet, STORY.text «مين قبض كام»
      line: "«حسابات نهائي 3» ولسه بتسأل مين قبض كام.",
      // DAY money body
      fix: "كل بيعة وكل دفعة وكل حركة مخزون بتتسجّل لوحدها في دفتر الأستاذ.",
      chapter: "money",
      link: "شوف الخزنة",
    },
  ],
};

// «جوّه الريّس». Each capture is a real screen; the module name is on screen
// in the capture itself (its page title and the sidebar).
export const INSIDE = {
  title: "جوّه الريّس",
  // HOW.steps[2] «تدخل على بورد الريّس»; the captures are the product's own screens.
  lead: "دي شاشات من البرنامج نفسه، زي ما هتشوفها بعد ما تسجّل.",
  // The head's far side, as in the reference; links to the module cards below.
  aside: "شوف الموديولز",
  items: [
    { capture: "board", kicker: "بورد الريّس", name: "الشركة كلها في صفحة" },
    { capture: "jobs", kicker: "المشاوير", name: "كل مشوار وحالته" },
    { capture: "stock", kicker: "العهدة", name: "الوحدات والكميات والعهدة" },
    { capture: "finance", kicker: "الخزنة", name: "محرّك المحاسبة" },
    { capture: "invoices", kicker: "الفواتير", name: "الفواتير والتحصيل" },
  ],
};

// Pricing band. Labels only: every number comes from the CMS overlay and is
// put together in data.js buildPriceCards.
export const PRICE_LABELS = {
  // The head's far side, as in the reference; links to #faq, where the trial,
  // the card and stopping are all answered.
  aside: "كل الأسئلة",
  // trialKicker comes from content.js trialPhrase: «الشهر المجاني», or «14 يوم».
  // FAQ "card": «{trialDays} يوم بكل المزايا» + «لأ» to a card
  trialTitle: "{trialDays} يوم بكل المزايا، من غير كارت",
  duration: "المدة",
  afterwards: "بعدها",
  // FAQ "card": «وبعدها تختار تدفع أو تقف»
  afterwardsValue: "تدفع أو تقف",
  monthlyTitle: "اشتراك شهري",
  yearlyTitle: "اشتراك سنوي",
  // Pages/Home/pricing.js SoloPlan badge; only when opensEverything() is true.
  everything: "كل حاجة مفتوحة",
  price: "السعر",
  payment: "الدفع",
  // FAQ "card": «كل فترة دفعة واحدة»
  paidMonthly: "دفعة كل شهر",
  paidYearly: "دفعة كل سنة",
  saved: "بتوفّر",
  monthlyCell: "شهري",
  yearlyCell: "سنوي",
  popular: "الأكثر اختيارًا", // Pages/Home/pricing.js
  finePrint: "بعد التجربة الدفع بمحفظة إلكترونية أو إنستاباي — كل فترة دفعة واحدة، من غير تجديد تلقائي.",
};

// «لمين». WHO keeps its eyebrow, title and notItems; these are the bento's labels.
// `focal` and `zoom` crop a photo so the same one reads as a different shot.
// Rooftop's top fifth holds a unit's emblem: every crop of it keeps that out
// (Assets/hero/CREDITS.md).
export const SECTORS = {
  // WHO.forItems[0]. One line, as in the reference; the tiles say the rest.
  lead: "عندك شركة تكييف في مصر، وبتدير أسطوات في الشارع.",
  tiles: [
    // WHO.forItems[3] «بتركّب للزباين»
    { id: "install", icon: ClipboardCheck, title: "شركات التركيب", photo: "rooftop", focal: "62% 100%", zoom: 1.3, href: "#day-jobs" },
    // index.html description «أوامر التركيب والصيانة»
    { id: "service", icon: Wrench, title: "الصيانة", photo: "install", focal: "27% 0%", zoom: 2.4, href: "#day-jobs" },
    // WHO.forItems[3] «بتبيع أجهزة لتجار»; DAY stock «حساب جاري لكل تاجر ومورد»
    { id: "traders", icon: Store, title: "تجارة الأجهزة للتجار", photo: "stock", focal: "50% 60%", href: "#day-stock" },
    // WHO.forItems[0] «بتدير أسطوات في الشارع»
    { id: "field", icon: Users, title: "فرق الأسطوات في الشارع", photo: "install", focal: "26% 100%", zoom: 1.7, href: "#day-techs" },
    // WHO.forItems[1] «واتساب ودفتر وإكسل ومكالمات»; DAY money
    { id: "accounts", icon: Wallet, title: "اللي حساباته لسه في إكسل", photo: "money", focal: "45% 50%", href: "#day-money" },
    // WHO.forItems[2] «الفلوس والعهدة والمشاوير من شاشة واحدة»
    { id: "one-screen", icon: LayoutDashboard, title: "الفلوس والعهدة والمشاوير من شاشة واحدة", capture: "board", focal: "50% 40%", href: "#day" },
  ],
};

// «إيه اللي بيتغير؟». Short on purpose: a two-word title and one line.
export const OUTCOMES = {
  title: "إيه اللي بيتغير؟",
  items: [
    // STORY.oneScreen; DAY jobs points[0] (the four job states)
    { id: "jobs", icon: LayoutDashboard, title: "شاشة واحدة", line: "المشاوير كلها قدامك وبحالتها." },
    // DAY stock title
    { id: "stock", icon: PackageSearch, title: "عهدة معروفة", line: "كل قطعة: مع مين، وامتى نزلت." },
    // DAY money body
    { id: "ledger", icon: BookCheck, title: "قيد لوحده", line: "كل بيعة ودفعة بتتسجّل لوحدها." },
    // DAY techs; FAQ "techs" «الأسطى ما بيشوفش فلوس الشركة»
    { id: "techs", icon: Smartphone, title: "أسطى بموبايله", line: "بيشوف شغله، مش الفلوس." },
  ],
};

// The FAQ band's copy beside the accordion. FAQ has a title and no subtitle.
export const FAQ_BAND = {
  // FAQ "card", "data", "techs", "stop"
  lead: "الكارت، والبيانات، والأسطوات، واللي بيحصل لو وقفت: كله مكتوب هنا قبل ما تسجّل.",
};

// Three module cards where the reference has articles. We have no blog and
// invent none: tag, title and body come from DAY.items by `chapter`.
export const MODULES = {
  title: "تلات موديولز، من جوّه",
  // The section's aside, to the whole «يوم الريّس» (DAY).
  more: "يوم الريّس كامل",
  linkLabel: "شوف الموديول",
  // `focal` and `zoom` crop each capture to its page: no sidebar and no top bar.
  items: [
    { chapter: "jobs", tag: "المشاوير", capture: "jobs", focal: "60% 27%", zoom: 1.5 },
    { chapter: "stock", tag: "العهدة", capture: "stock", focal: "60% 27%", zoom: 1.5 },
    { chapter: "money", tag: "الخزنة", capture: "invoices", focal: "60% 27%", zoom: 1.5 },
  ],
};

export const FOOTER = {
  // Shared/Footer.js, word for word.
  descriptor: "الريّس بيجمّع المشاوير والأسطوات والعهدة والخزنة في مكان واحد، عشان تمسك شغل الشركة كله من شاشة واحدة.",
  // Shared/Footer.js: the module vocabulary of BRAND.md §5, plain text and not
  // links. «الكشف» stays out until a preventive-maintenance screen backs it.
  modulesTitle: "الشغل",
  modules: ["المشاوير", "الأسطوات", "العهدة", "الخزنة", "الزباين", "الفواتير"],
  platformTitle: "المنصة",
  contactTitle: "تواصل معنا",
  rights: "الريّس — كل الحقوق محفوظة.", // Shared/Footer.js
};

// «يوم الريّس». DAY has the chapters; these are the labels around them.
export const PILLARS = {
  // DAY.subtitle «كل محطة شاشة من جوّه الريّس»; links to the screens (#inside).
  asideLabel: "شوف الشاشات",
  railLabel: "محطات اليوم",
};
